import { can } from '@misumi/shared'
import { useEffect, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '../lib/auth/auth-context'
import { useCartCount } from '../lib/cart/cart-count-context'
import { clearRecentlyViewed } from '../verticals/catalog/recently-viewed-store'
import { roleLabel } from './admin/roleLabels'
import { Icon } from './ui/Icon'

/**
 * Sticky 64px top bar (components.md "Top bar"; UID-27 global-chrome
 * requirement 1): search field + dropdown panel, cart, account menu.
 *
 * The search box and its dropdown are built to the mock's STRUCTURE (input +
 * "AI" chip + filter icon, panel with a recent-searches/browse-categories
 * layout); the dropdown's recent-searches/browse-categories content is still
 * unwired (no backend support for view history yet). Submitting a query
 * (UID-251) navigates to `/catalog/search?q=...`, the keyword-search results
 * screen — see `CatalogSearchPage.tsx`.
 */
export interface TopBarProps {
  /** Opens the ≤900px off-canvas sidebar drawer (owned by AppShell). Optional
   * so TopBar's own presentational tests need not stub it. */
  readonly onOpenMobileNav?: () => void
}

export function TopBar({ onOpenMobileNav }: TopBarProps = {}) {
  const auth = useAuth()
  const navigate = useNavigate()
  const { lineCount } = useCartCount()
  const hasCartItems = lineCount !== undefined && lineCount > 0
  const cartItemNoun = lineCount === 1 ? 'item' : 'items'
  const cartAriaLabel = hasCartItems ? `Cart, ${lineCount} ${cartItemNoun}` : 'Cart'
  const [query, setQuery] = useState('')
  const [searchOpen, setSearchOpen] = useState(false)
  const searchWrapperRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (!searchOpen) return
    function handlePointerDown(event: MouseEvent) {
      if (!searchWrapperRef.current?.contains(event.target as Node)) {
        setSearchOpen(false)
      }
    }
    document.addEventListener('mousedown', handlePointerDown)
    return () => document.removeEventListener('mousedown', handlePointerDown)
  }, [searchOpen])

  return (
    <header className="topbar">
      {/* Mobile-only (≤900px) hamburger that opens the off-canvas sidebar
          drawer. Hidden on desktop via shell.css, where the sidebar is
          always in-flow. */}
      <button
        type="button"
        className="topbar-nav-toggle"
        aria-label="Open navigation"
        onClick={onOpenMobileNav}
      >
        <Icon name="menu" />
      </button>
      <div className="topbar-search" ref={searchWrapperRef}>
        <form
          onSubmit={(event) => {
            event.preventDefault()
            const trimmed = query.trim()
            if (trimmed.length === 0) return
            setSearchOpen(false)
            navigate(`/catalog/search?q=${encodeURIComponent(trimmed)}`)
          }}
        >
          <Icon name="search" className="topbar-search-icon" />
          <label htmlFor="topbar-search-input" className="sr-only">
            Search parts, materials, or part numbers
          </label>
          <input
            id="topbar-search-input"
            type="search"
            className="topbar-search-input"
            placeholder="Search parts, materials, or part numbers…"
            value={query}
            onFocus={() => setSearchOpen(true)}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Escape') setSearchOpen(false)
            }}
          />
          <span className="topbar-search-ai-chip" aria-hidden="true">
            AI
          </span>
          <button
            type="button"
            className="topbar-search-filter"
            aria-label="Search filters"
            disabled
          >
            <Icon name="filter" />
          </button>
        </form>
        {searchOpen && (
          <div className="topbar-search-dropdown" role="presentation">
            <p className="topbar-search-dropdown-hint caption-text">
              Start typing to search the MISUMI catalog and your Fictiv library.
            </p>
          </div>
        )}
      </div>

      <div className="topbar-spacer" />

      {/* UID-244 (2026-08-23 design refresh, README §5): a dedicated top-bar
          Upload entry point, between the search field and the cart — mirrors
          the design source's `startStaging` button exactly (Fictiv
          Platform.dc.html:366). Styled with the same `btn btn-outline btn-md`
          classes TopBar already applies directly to the "Sign in" link below,
          rather than introducing a new button variant for one call site.

          Gated on `bom:upload` (engineer-only, authz.ts) to MATCH the sidebar
          Uploads nav (registry.tsx). Uploads is an engineer act; without this
          gate a buyer/purchaser saw an Upload button the sidebar hides, and
          clicking it landed them on /uploads — a screen they have no capability
          for — which then failed to load recent uploads. */}
      {auth.status === 'authenticated' && can(auth.session.accountContext.role, 'bom:upload') && (
        <Link
          to="/uploads"
          className="topbar-upload-link btn btn-outline btn-md"
          aria-label="Upload"
        >
          <Icon name="upload" />
          <span className="topbar-upload-label">Upload</span>
        </Link>
      )}

      {/* Cart lives in the global header (screens.md §1 global chrome), not the
          left rail. The badge shows the current line count once the cart has
          loaded this session (see cart-count-context) — `undefined` or 0 renders
          no badge so an empty cart isn't advertised. */}
      <Link to="/cart" className="topbar-icon-btn topbar-cart-link" aria-label={cartAriaLabel}>
        <Icon name="cart" />
        {lineCount !== undefined && lineCount > 0 && (
          <span className="topbar-cart-badge" aria-hidden="true">
            {lineCount > 99 ? '99+' : lineCount}
          </span>
        )}
      </Link>

      {auth.status === 'authenticated' ? (
        <AccountMenu />
      ) : auth.status === 'loading' ? null : (
        <Link to="/login" className="app-signin btn btn-outline btn-md">
          Sign in
        </Link>
      )}
    </header>
  )
}

function AccountMenu() {
  const auth = useAuth()
  const [open, setOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (!open) return
    function handlePointerDown(event: MouseEvent) {
      if (!menuRef.current?.contains(event.target as Node)) {
        setOpen(false)
      }
    }
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', handlePointerDown)
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('mousedown', handlePointerDown)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [open])

  if (auth.status !== 'authenticated') return null

  const displayName = auth.session.displayName || auth.session.email
  const initials = displayName.trim().charAt(0).toUpperCase()

  return (
    <div className="account-menu" ref={menuRef}>
      {/* UID-301 (Fictiv Platform.dc.html:373–376): the resting top-bar chrome
          is ONLY a bordered pill — avatar initial + chevron. The identity
          (name/email) and the server-derived role indicator move INTO the
          dropdown header below, matching the design; they were previously
          always-visible beside the avatar (UID-9/UID-130). */}
      <button
        type="button"
        className="account-menu-trigger"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Account menu for ${auth.session.email}`}
        onClick={() => setOpen((current) => !current)}
      >
        <span className="account-avatar" aria-hidden="true">
          {initials}
        </span>
        <Icon name="chevron-down" />
      </button>
      {open && (
        <div role="menu" className="account-menu-panel">
          {/* Identity header (Fictiv Platform.dc.html account menu): avatar +
              name + email. UID-130: show the person's name (or email), never
              the opaque account UUID. */}
          <div className="account-menu-header">
            <span className="account-avatar account-avatar-lg" aria-hidden="true">
              {initials}
            </span>
            <span className="account-menu-identity">
              <span className="account-menu-name">{displayName}</span>
              <span className="account-menu-email-line">{auth.session.email}</span>
            </span>
          </div>
          {/* Account-context section. The design shows the COMPANY here, but
              the session carries no company name (only an opaque account id,
              C2) — so we surface the server-derived ROLE instead, preserving
              the UID-9 "which role am I in" affordance and its testid. UID-343:
              accepted as the permanent substitute, not a pending contract gap —
              a `company` field was assessed and deliberately not pursued. */}
          <div className="account-menu-context" data-testid="role-indicator">
            <span className="account-menu-context-label">Role</span>
            <span className="account-menu-context-value">
              {roleLabel(auth.session.accountContext.role)}
            </span>
          </div>
          {/* UID-343: "Manage users" and "Billing" moved to /account/settings —
              the design's dropdown (Fictiv Platform.dc.html account menu) has
              no equivalent items here, only Account settings + Log out. */}
          <Link
            role="menuitem"
            className="account-menu-item"
            to="/account/settings"
            onClick={() => setOpen(false)}
          >
            <Icon name="settings" />
            Account settings
          </Link>
          <button
            role="menuitem"
            type="button"
            className="account-menu-item account-menu-item-danger"
            onClick={() => {
              // Client-only "Recently viewed" history must not survive a
              // logout (the user's explicit call) — cleared here at the
              // one real UI logout entry point, alongside the server-side
              // session end.
              clearRecentlyViewed()
              void auth.logout()
            }}
          >
            <Icon name="log-out" />
            Log out
          </button>
        </div>
      )}
    </div>
  )
}
